import { ReactNode, useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import { money } from '../../../utils/currency';
import { loadSettings } from '../../../modules/settings/services/settingsService';
import { Card } from '../../ui/Display/Display';
import { Icon } from '../../ui/Icon/Icon';
import logoLight from '../../../assets/logo-light.svg';

const address = (a) => (a ? [a.line, a.city, a.pincode].filter(Boolean).join(', ') : '');

/** Company letterhead from Settings. */
function Letterhead({ kind, number }: { kind: string; number?: string }) {
  const [s, setS] = useState<any>(null);
  useEffect(() => {
    loadSettings().then(setS).catch(() => {});
  }, []);
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div>
          <img src={logoLight} alt="Electroz" className="mb-1 h-8 w-auto" />
          <p className="text-[13px] text-ink-500">{[address(s?.address), s?.phone].filter(Boolean).join(' · ') || 'Electrical contracting, supply and maintenance'}</p>
          {s?.gstin && <p className="font-mono text-xs text-ink-400">GSTIN {s.gstin}</p>}
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-600">{kind}</p>
        <p className="text-xl font-semibold tracking-tight text-navy-900">{number}</p>
      </div>
    </div>
  );
}

/**
 * Printable-looking preview of a quotation or invoice.
 * party: customer snapshot. job: { title, site }. dates: [[label, value]].
 * sections: [{ title, lines: [{ key, text, sub, qty, unit, rate, gstRate, amount, byCustomer }] }]
 */
export interface DocLine {
  key: string;
  text: ReactNode;
  sub?: ReactNode;
  qty: number | string;
  unit?: string;
  rate: number;
  gstRate?: number;
  amount?: number;
  byCustomer?: boolean;
}

export interface DocPreviewProps {
  kind: string;
  number?: string;
  party?: any;
  job?: { title?: string; site?: any } | null;
  dates?: [label: string, value: ReactNode][];
  sections: { title: string; lines: DocLine[]; note?: ReactNode }[];
  terms?: string | null;
  totals?: any;
  isGst?: boolean;
  footerNote?: ReactNode;
}

export function DocPreview({ kind, number, party, job, dates = [], sections, terms, totals, isGst, footerNote }: DocPreviewProps) {
  return (
    <Card className="p-5 sm:p-8">
      <Letterhead kind={kind} number={number} />

      <div className="mt-6 grid overflow-hidden rounded-lg border border-line bg-navy-50/50 sm:grid-cols-3 sm:divide-x sm:divide-line">
        <div className="p-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-400">Prepared for</p>
          <p className="mt-1 font-semibold text-navy-900">{party?.name}</p>
          {party?.phone && <p className="text-[13px] text-ink-500">{party.phone}</p>}
          {address(party?.address) && <p className="text-[13px] text-ink-500">{address(party.address)}</p>}
          {party?.gstin && <p className="font-mono text-xs text-ink-400">GSTIN {party.gstin}</p>}
        </div>
        <div className="border-t border-line p-4 sm:border-t-0">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-400">Job</p>
          <p className="mt-1 font-semibold text-navy-900">{job?.title || '-'}</p>
          {job?.site && <p className="text-[13px] text-ink-500">Site: {job.site}</p>}
        </div>
        <dl className="space-y-1.5 border-t border-line p-4 text-[13px] sm:border-t-0">
          {dates.filter(Boolean).map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <dt className="text-ink-500">{label}</dt>
              <dd className="text-right font-medium text-navy-900">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {sections
        .filter((s) => s.lines.length)
        .map((s) => (
          <section key={s.title} className="mt-7">
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="text-[15px] font-semibold">{s.title}</h3>
              <span className="text-xs text-ink-400">{s.lines.length} items</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="border-b-2 border-navy-800 text-[11px] uppercase tracking-wider text-ink-500">
                  <tr>
                    <th scope="col" className="w-8 py-2 font-medium">#</th>
                    <th scope="col" className="py-2 font-medium">Item</th>
                    <th scope="col" className="py-2 text-right font-medium">Qty</th>
                    <th scope="col" className="py-2 text-right font-medium">Rate</th>
                    {isGst && <th scope="col" className="py-2 text-right font-medium">GST</th>}
                    <th scope="col" className="py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="tabular divide-y divide-line">
                  {s.lines.map((l, i) => (
                    <tr key={l.key ?? i}>
                      <td className="py-3 align-top text-ink-400">{i + 1}</td>
                      <td className="py-3 align-top">
                        <p className="font-medium text-navy-900">{l.text}</p>
                        {l.sub && <p className="text-xs text-ink-400">{l.sub}</p>}
                      </td>
                      <td className="whitespace-nowrap py-3 text-right align-top">{l.qty} {l.unit}</td>
                      <td className="py-3 text-right align-top">{l.byCustomer ? '-' : money(l.rate)}</td>
                      {isGst && <td className="py-3 text-right align-top text-ink-500">{l.byCustomer ? '-' : `${l.gstRate ?? 0}%`}</td>}
                      <td className={cn('py-3 text-right align-top font-semibold', l.byCustomer && 'font-normal text-ink-400')}>
                        {l.byCustomer ? 'By customer' : money(l.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}

      <div className="mt-8 grid gap-6 border-t border-line pt-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
        <div>
          {terms && (
            <>
              <p className="text-[11px] font-medium uppercase tracking-wider text-ink-400">Terms</p>
              <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-ink-600">{terms}</p>
            </>
          )}
          {footerNote}
        </div>
        {totals}
      </div>
    </Card>
  );
}

/** Horizontal progress steps. steps: [{ key, label, icon }]; current is the index reached. */
export function Stepper({ steps, current, tone = 'brand' }: { steps: { key: string; label: string; icon?: string }[]; current: number; tone?: string }) {
  return (
    <Card className="px-5 pb-3 pt-4">
      <ol className="grid gap-2" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        {steps.map((s, i) => {
          const done = i <= current;
          return (
            <li key={s.key} className="min-w-0">
              <span className={cn('block h-1 rounded-full', done ? (tone === 'red' && i === current ? 'bg-red-500' : 'bg-brand-600') : 'bg-navy-100')} />
              <span className={cn('mt-2 flex items-center gap-1.5 truncate text-[13px]', done ? 'font-medium text-navy-900' : 'text-ink-400')}>
                <Icon name={s.icon ?? 'check'} size={15} className="hidden shrink-0 sm:block" />
                <span className="truncate">{s.label}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

export const siteText = address;
